import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-signature-pad',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './signature-pad.component.html',
    host: { class: 'block' }
})
export class SignaturePadComponent implements AfterViewInit, OnDestroy {
    @Input() width: number = 500;
    @Input() height: number = 200;
    @Input() penColor: string = '#000000';
    @Input() penWidth: number = 1.5;
    @Input() placeholder: string = 'Signature';
    @Input() backgroundColor: string = '#ffffff';

    @Output() signatureChange = new EventEmitter<File | null>();

    @ViewChild('signatureCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

    private ctx!: CanvasRenderingContext2D;
    private isDrawing = false;
    private lastX = 0;
    private lastY = 0;
    private hasStrokes = false;

    isEmpty = true;

    private resizeObserver?: ResizeObserver;

    ngAfterViewInit(): void {
        const canvas = this.canvasRef.nativeElement;
        this.ctx = canvas.getContext('2d')!;
        this.fitCanvasToContainer();
        this.clear();

        this.resizeObserver = new ResizeObserver(() => this.fitCanvasToContainer());
        this.resizeObserver.observe(canvas.parentElement!);
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    private fitCanvasToContainer(): void {
        const canvas = this.canvasRef.nativeElement;
        const parent = canvas.parentElement!;
        const rect = parent.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;

        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;

        this.ctx.scale(dpr, dpr);
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        this.ctx.strokeStyle = this.penColor;
        this.ctx.lineWidth = this.penWidth;

        if (!this.hasStrokes) {
            this.fillBackground();
        }
    }

    private fillBackground(): void {
        const canvas = this.canvasRef.nativeElement;
        const dpr = window.devicePixelRatio || 1;
        this.ctx.save();
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.fillStyle = this.backgroundColor;
        this.ctx.fillRect(0, 0, canvas.width, canvas.height);
        this.ctx.restore();
    }

    // --- Mouse events ---
    onMouseDown(e: MouseEvent): void {
        this.startDrawing(e.offsetX, e.offsetY);
    }

    onMouseMove(e: MouseEvent): void {
        if (!this.isDrawing) return;
        this.draw(e.offsetX, e.offsetY);
    }

    onMouseUp(): void {
        this.stopDrawing();
    }

    // --- Touch events ---
    onTouchStart(e: TouchEvent): void {
        e.preventDefault();
        const touch = e.touches[0];
        const pos = this.getTouchPos(touch);
        this.startDrawing(pos.x, pos.y);
    }

    onTouchMove(e: TouchEvent): void {
        e.preventDefault();
        if (!this.isDrawing) return;
        const touch = e.touches[0];
        const pos = this.getTouchPos(touch);
        this.draw(pos.x, pos.y);
    }

    onTouchEnd(e: TouchEvent): void {
        e.preventDefault();
        this.stopDrawing();
    }

    private getTouchPos(touch: Touch): { x: number; y: number } {
        const canvas = this.canvasRef.nativeElement;
        const rect = canvas.getBoundingClientRect();
        return {
            x: touch.clientX - rect.left,
            y: touch.clientY - rect.top
        };
    }

    private startDrawing(x: number, y: number): void {
        this.isDrawing = true;
        this.lastX = x;
        this.lastY = y;
        this.ctx.beginPath();
        this.ctx.moveTo(x, y);
    }

    private draw(x: number, y: number): void {
        this.ctx.strokeStyle = this.penColor;
        this.ctx.lineWidth = this.penWidth;
        this.ctx.lineTo(x, y);
        this.ctx.stroke();
        this.ctx.beginPath();
        this.ctx.moveTo(x, y);
        this.lastX = x;
        this.lastY = y;
        this.hasStrokes = true;
        this.isEmpty = false;
    }

    private stopDrawing(): void {
        if (this.isDrawing) {
            this.isDrawing = false;
            this.ctx.closePath();
            this.emitImage();
        }
    }

    clear(): void {
        this.hasStrokes = false;
        this.isEmpty = true;
        this.fillBackground();
        this.signatureChange.emit(null);
    }

    private emitImage(): void {
        const canvas = this.canvasRef.nativeElement;
        const base64 = canvas.toDataURL('image/png');
        // Convert base64 to File
        const byteString = atob(base64.split(',')[1]);
        const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeString });
        const file = new File([blob], 'signature.png', { type: mimeString });
        this.signatureChange.emit(file);
    }

    getSignatureImage(): string | null {
        if (this.isEmpty) return null;
        return this.canvasRef.nativeElement.toDataURL('image/png');
    }
}